/**
 * Widget Registry — widgets que a Personalização da Home pode oferecer.
 *
 * Cada widget = componente React que recebe perfilUsuario (PerfilUsuario).
 * Usa useWidgetData + WidgetShell pra padronizar loading/error/empty.
 *
 * **FASE-C — regra "se não funciona, ou termina ou some":** 11 widgets
 * existiam só como casca com selo "beta" e o texto 🚧 "aguarda implementação
 * backend" (KPI-2, KPI-4, CHART-2, CHART-4, LISTA-2, LISTA-4, FIXO-3 e os
 * quatro CLI-*). O montador de Home não pode oferecer o que não existe: eles
 * saíram do registry, do renderer-map e da galeria. Sobraram os 8 que buscam
 * dado de verdade (ou executam uma ação de verdade). Quando o endpoint de um
 * deles existir, o widget volta implementado — não como casca.
 *
 * Fontes de dados dos widgets que ficaram:
 *   - /api/v1/deals?workflowSlug=                    → KPI-1, LISTA-1, CHART-1
 *   - /api/v1/admin/financeiro/faturamento-mensal    → CHART-3
 *   - /api/v1/notifications                          → FIXO-1
 *   - /api/v1/tasks                                  → FIXO-2
 *   - ACAO-1 (abrir chamado) e ACAO-3 (buscar cliente) navegam pra tela real
 *
 * NÃO REVERTER:
 *   - useWidgetData lida com mount cleanup pra evitar setState após unmount
 *   - WidgetShell distingue loading/error/empty visualmente
 */
import { useNavigate } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Bell, BarChart3, ClipboardList, LineChart as LineChartIcon, Search, Zap } from 'lucide-react';

import { api } from '@/lib/api';
import { hojeBR } from '@/lib/datas-brasilia';

import { useWidgetData } from './useWidgetData';
import { WidgetShell } from './WidgetShell';

// FASE-C: ícone dos widgets deixou de ser emoji (📊📈📋⚡📌) — o design system
// é Lucide-only. Tamanho fixo pra alinhar com o título do WidgetShell.
const ICO = 'w-4 h-4';

export type PerfilUsuario =
  | 'admin'
  | 'comercial'
  | 'administrativo'
  | 'tecnico'
  | 'cliente_gestor'
  | 'cliente_portaria';

interface WidgetProps {
  perfilUsuario: PerfilUsuario;
}

function fmtBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// ── KPI Widgets ─────────────────────────────────────────────────────────

export function Kpi1ChamadosHoje(_props: WidgetProps) {
  const { data, loading, error } = useWidgetData(
    async () => {
      // Conta deals (chamados = workflows manutenção corretiva) com criado hoje
      const r = await api.get<{ items: Array<{ created_at?: string }> }>(
        `/api/v1/deals?workflowSlug=corretiva&limit=200`,
      );
      const hoje = hojeBR();
      const total = (r.items ?? []).filter((d) =>
        d.created_at?.startsWith(hoje),
      ).length;
      return { total };
    },
    60_000,
  );

  return (
    <WidgetShell
      title="Chamados abertos hoje"
      icon={<BarChart3 className={ICO} />}
      loading={loading}
      error={error}
      testId="widget-kpi-1"
    >
      <div className="h-full flex flex-col items-center justify-center p-3">
        <div className="text-4xl font-bold text-info">{data?.total ?? 0}</div>
        <div className="text-xs text-ink-3 dark:text-ink-dark-3 mt-1">
          chamados criados hoje
        </div>
      </div>
    </WidgetShell>
  );
}

// ── Chart Widgets ───────────────────────────────────────────────────────

export function Chart1Chamados30d(_props: WidgetProps) {
  const { data, loading, error } = useWidgetData(async () => {
    // Aggrega deals corretiva por dia nos últimos 30d
    const r = await api.get<{ items: Array<{ created_at?: string }> }>(
      `/api/v1/deals?workflowSlug=corretiva&limit=1000`,
    );
    const buckets = new Map<string, number>();
    const now = new Date();
    for (let i = 0; i < 30; i++) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      buckets.set(d.toISOString().split('T')[0], 0);
    }
    for (const it of r.items ?? []) {
      if (!it.created_at) continue;
      const day = it.created_at.split('T')[0];
      if (buckets.has(day)) buckets.set(day, (buckets.get(day) ?? 0) + 1);
    }
    return Array.from(buckets.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dia, total]) => ({ dia: dia.slice(5), total }));
  }, 60_000);

  return (
    <WidgetShell
      title="Chamados últimos 30 dias"
      icon={<LineChartIcon className={ICO} />}
      loading={loading}
      error={error}
      empty={data?.length === 0}
      testId="widget-chart-1"
    >
      <div className="w-full h-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data ?? []} margin={{ top: 8, right: 8, bottom: 24, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3DCD0" />
            <XAxis dataKey="dia" fontSize={12} />
            <YAxis fontSize={12} allowDecimals={false} />
            <Tooltip />
            <Line type="monotone" dataKey="total" stroke="#234E6B" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </WidgetShell>
  );
}

export function Chart3Faturamento6m(_props: WidgetProps) {
  const { data, loading, error } = useWidgetData(async () => {
    // Faturamento dos últimos 6 meses via /financeiro/faturamento-mensal
    // (endpoint enxuto do Redesign Financeiro — o engine de KPIs antigo saiu).
    const result: Array<{ mes: string; valor: number }> = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now);
      d.setMonth(d.getMonth() - i);
      const anoMes = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      try {
        const k = await api.get<{ faturamentoCentavos: number }>(
          `/api/v1/admin/financeiro/faturamento-mensal?anoMes=${anoMes}`,
        );
        result.push({ mes: anoMes.slice(2), valor: (k.faturamentoCentavos ?? 0) / 100 });
      } catch {
        result.push({ mes: anoMes.slice(2), valor: 0 });
      }
    }
    return result;
  });

  return (
    <WidgetShell
      title="Faturamento últimos 6 meses"
      icon={<LineChartIcon className={ICO} />}
      loading={loading}
      error={error}
      empty={data?.every((x) => x.valor === 0)}
      emptyLabel="Sem faturamento registrado"
      testId="widget-chart-3"
    >
      <div className="w-full h-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data ?? []} margin={{ top: 8, right: 8, bottom: 24, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E3DCD0" />
            <XAxis dataKey="mes" fontSize={12} />
            <YAxis
              fontSize={12}
              tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v))}
            />
            <Tooltip formatter={(v) => fmtBRL(v as number)} />
            <Bar dataKey="valor" fill="#3F6832" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </WidgetShell>
  );
}

// ── Lista Widgets ───────────────────────────────────────────────────────

export function Lista1MeusChamados(_props: WidgetProps) {
  const { data, loading, error } = useWidgetData(async () => {
    const r = await api.get<{
      items: Array<{ id: string; titulo?: string; cliente?: string; created_at?: string }>;
    }>(`/api/v1/deals?workflowSlug=corretiva&limit=10`);
    return r.items ?? [];
  }, 60_000);

  return (
    <WidgetShell
      title="Meus chamados"
      icon={<ClipboardList className={ICO} />}
      loading={loading}
      error={error}
      empty={data?.length === 0}
      emptyLabel="Nenhum chamado atribuído"
      testId="widget-lista-1"
    >
      <div className="h-full overflow-y-auto p-2 space-y-1">
        {(data ?? []).map((it) => (
          <div
            key={it.id}
            className="text-xs bg-paper-warm dark:bg-paper-dark-warm hover:bg-line-soft dark:hover:bg-paper-dark-elevated rounded p-2 text-ink-1 dark:text-ink-dark-1"
            data-testid={`lista1-item-${it.id}`}
          >
            <div className="font-semibold truncate">{it.titulo ?? '(sem título)'}</div>
            <div className="text-ink-3 dark:text-ink-dark-3 truncate">
              {it.cliente ?? '(cliente?)'}
            </div>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

// ── Ação Widgets ────────────────────────────────────────────────────────

export function Acao1NovoChamado(_props: WidgetProps) {
  const navigate = useNavigate();
  return (
    <WidgetShell title="Novo chamado" icon={<Zap className={ICO} />} testId="widget-acao-1">
      <div className="h-full flex items-center justify-center p-4">
        <button
          onClick={() => navigate('/menu/manutencao/corretiva?openNew=1')}
          data-testid="acao-1-btn"
          className="px-4 py-3 bg-red hover:bg-red-deep text-white rounded-lg font-semibold text-sm"
        >
          + Abrir chamado
        </button>
      </div>
    </WidgetShell>
  );
}

export function Acao3BuscarCliente(_props: WidgetProps) {
  const navigate = useNavigate();
  return (
    <WidgetShell title="Buscar cliente" icon={<Zap className={ICO} />} testId="widget-acao-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget);
          const q = String(fd.get('q') ?? '').trim();
          if (q) navigate(`/admin/clientes-v2?search=${encodeURIComponent(q)}`);
        }}
        className="h-full flex items-center p-3"
      >
        {/* 🔴 #522 (13/10/2026): em 768 px o campo e o botão saíam do cartão. Um <input> tem largura mínima PRÓPRIA (a
            do atributo `size`), e `flex-1` sozinho não o deixa encolher: o `min-w-0` deixa. O botão não encolhe. */}
        <input
          name="q"
          type="search"
          placeholder="CNPJ ou razão social…"
          data-testid="acao-3-input"
          className="min-w-0 flex-1 border border-line-soft dark:border-line-soft-dark bg-paper-warm dark:bg-paper-dark-warm text-ink-1 dark:text-ink-dark-1 placeholder-ink-3 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-red"
        />
        <button
          type="submit"
          aria-label="Buscar cliente"
          data-testid="acao-3-btn"
          className="ml-2 flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center px-3 py-1.5 bg-red hover:bg-red-deep text-white text-sm rounded"
        >
          <Search className={ICO} />
        </button>
      </form>
    </WidgetShell>
  );
}

// ── Fixo Widgets ────────────────────────────────────────────────────────

export function Fixo1Notificacoes(_props: WidgetProps) {
  const { data, loading, error } = useWidgetData(async () => {
    const r = await api.get<{
      items: Array<{ id: string; title?: string; body?: string; read_at?: string | null }>;
    }>(`/api/v1/notifications?limit=10`);
    return r.items ?? [];
  }, 30_000);

  const unread = (data ?? []).filter((n) => !n.read_at);

  return (
    <WidgetShell
      title="Notificações"
      subtitle={unread.length > 0 ? `${unread.length} não lidas` : undefined}
      icon={<Bell className={ICO} />}
      loading={loading}
      error={error}
      empty={data?.length === 0}
      emptyLabel="Sem notificações"
      testId="widget-fixo-1"
    >
      <div className="h-full overflow-y-auto p-2 space-y-1">
        {(data ?? []).map((n) => (
          <div
            key={n.id}
            className={`text-xs rounded p-2 text-ink-1 dark:text-ink-dark-1 ${
              n.read_at
                ? 'bg-paper-warm dark:bg-paper-dark-warm'
                : 'bg-info-tint dark:bg-[rgba(35,78,107,0.35)] border-l-2 border-info dark:border-info/70'
            }`}
          >
            <div className="font-semibold truncate">{n.title ?? '(sem título)'}</div>
            {n.body && (
              <div className="text-ink-2 dark:text-ink-dark-2 truncate">{n.body}</div>
            )}
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

export function Fixo2TarefasHoje(_props: WidgetProps) {
  const { data, loading, error } = useWidgetData(async () => {
    const r = await api.get<{
      items: Array<{ id: string; titulo?: string; prazo_iso?: string; status?: string }>;
    }>(`/api/v1/tasks?limit=20`);
    const hoje = hojeBR();
    return (r.items ?? []).filter(
      (t) => t.status !== 'concluida' && t.prazo_iso?.startsWith(hoje),
    );
  }, 60_000);

  return (
    <WidgetShell
      title="Tarefas hoje"
      subtitle={data ? `${data.length} pendentes` : undefined}
      icon={<Bell className={ICO} />}
      loading={loading}
      error={error}
      empty={data?.length === 0}
      emptyLabel="Sem tarefas pra hoje"
      testId="widget-fixo-2"
    >
      <div className="h-full overflow-y-auto p-2 space-y-1">
        {(data ?? []).map((t) => (
          <div
            key={t.id}
            className="text-xs bg-paper-warm dark:bg-paper-dark-warm hover:bg-line-soft dark:hover:bg-paper-dark-elevated rounded p-2 text-ink-1 dark:text-ink-dark-1"
          >
            <div className="font-semibold truncate">{t.titulo ?? '(sem título)'}</div>
          </div>
        ))}
      </div>
    </WidgetShell>
  );
}

// Registry vive em renderer-map.ts (Fast Refresh exige só componentes em .tsx)
